import { zodResolver } from '@hookform/resolvers/zod'
import { Checkbox } from '@progress/kendo-react-inputs'
import { useAtomValue } from 'jotai'
import { useResetAtom } from 'jotai/utils'
import React from 'react'
import { Controller, type SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'

import { ExecuteCancelDialog } from '~/components/elements/ExecuteCancelDialog'
import { VStack } from '~/components/elements/VStack'
import { useLogEvent } from '~/hooks/useLogEvent'
import { ensureExists, mapItemsToObj } from '~/utils'

import { dialogInfoAtom } from '../atoms'
import { useCloseDialogWithRefresh } from '../hooks/useCloseDialogWithRefresh'
import { useMainQuerySurveys } from '../hooks/useMainQuery'
import { useUpdateSurveyGroupMutation } from '../hooks/useUpdateSurveyGroupMutation'

const schema = z.object({ checkedMap: z.record(z.string(), z.boolean()) })
type InputValues = z.infer<typeof schema>
const resolver = zodResolver(schema)

export const SurveySettingsDialog = () => {
  const { t } = useTranslation()
  const dialogInfo = useAtomValue(dialogInfoAtom)
  const resetDialogInfo = useResetAtom(dialogInfoAtom)
  const surveyGroup = ensureExists(
    dialogInfo?.surveyGroup,
    t('assertion_failed'),
  )
  const surveys = useMainQuerySurveys()

  const {
    control,
    formState: { isDirty },
    handleSubmit,
  } = useForm<InputValues>({
    defaultValues: {
      checkedMap: mapItemsToObj(surveys, (survey) =>
        surveyGroup.surveys.some(({ id }) => id === survey.id),
      ),
    },
    resolver,
  })

  const closeDialogWithRefresh = useCloseDialogWithRefresh()
  const updateSurveyGroupMutation = useUpdateSurveyGroupMutation({
    onSuccess: closeDialogWithRefresh,
  })

  const logEvent = useLogEvent()
  const handleValid = React.useCallback<SubmitHandler<InputValues>>(
    ({ checkedMap }) => {
      logEvent('Define survey setting')

      if (!isDirty) {
        resetDialogInfo()

        return
      }

      updateSurveyGroupMutation.mutate({
        surveyGroupId: surveyGroup.id,
        attachInput: surveys.map((survey) => ({
          surveyId: survey.id,
          on: checkedMap[survey.id],
        })),
      })
    },
    [
      isDirty,
      logEvent,
      resetDialogInfo,
      surveyGroup.id,
      surveys,
      updateSurveyGroupMutation,
    ],
  )

  return (
    <ExecuteCancelDialog
      executing={updateSurveyGroupMutation.isPending}
      mode="settings"
      title={t('surveygroup_title_surveysettings', { surveyGroup })}
      onCancel={resetDialogInfo}
      onExecute={handleSubmit(handleValid)}
    >
      <VStack spacing={2}>
        {surveys.map((survey) => (
          <Controller
            key={survey.id}
            control={control}
            name={`checkedMap.${survey.id}`}
            render={({ field }) => <Checkbox label={survey.title} {...field} />}
          />
        ))}
      </VStack>
    </ExecuteCancelDialog>
  )
}
